import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PlanClusterUrl {
  id: string;
  urlId: string;
  url: string;
  source: 'seed' | 'manual' | 'bulk';
  createdAt: string;
  analyzedAt?: string | null;
  onpageScore?: number | null;
  wordCount?: number | null;
  statusCode?: number | null;
  criticalIssuesCount?: number;
  warningsCount?: number;
  topicSignalsCount?: number;
  rankedKeywordsCount?: number;
  estimatedOrganicTraffic?: number | null;
  topicWeight?: number | null;
  topicRelevanceScore?: number | null;
  topicRelevancePct?: number | null;
  topicClass?: 'core' | 'supporting' | 'mention' | null;
  topicClassComputed?: 'core' | 'mention' | null;
  topicClassOverride?: 'core' | 'supporting' | 'mention' | null;
}

export interface PlanCluster {
  id: string;
  topicSlug: string;
  label: string;
  isDefaultSeed: boolean;
  seedVersion: number;
  createdAt: string;
  urls: PlanClusterUrl[];
}

export interface PlanTopicsResponse {
  success: boolean;
  data: {
    activeDomain: string;
    created?: boolean;
    topics: PlanCluster[];
  };
  timestamp: string;
}

export interface PlanTopicCandidatesResponse {
  success: boolean;
  data: {
    activeDomain: string;
    topicId: string;
    topicSlug: string;
    rows: Array<{
      urlId: string;
      analysisId: string;
      url: string;
      analyzedAt: string;
      onpageScore: number;
      wordCount: number;
      statusCode: number;
      criticalIssuesCount: number;
      warningsCount: number;
      topicSignalsCount: number;
      topicWeight: number;
      topicRelevanceScore: number;
      topicRelevancePct: number;
      topicClass: 'core' | 'mention';
      rankedKeywordsCount: number;
      estimatedOrganicTraffic: number | null;
      inventoryInclusionState?: 'default' | 'force_include' | 'force_exclude';
    }>;
  };
  timestamp: string;
}

export type InventoryTrackingFilter = 'tracked' | 'excluded' | 'all';
export type PlanClusterClassValue = 'core' | 'supporting' | 'mention' | 'auto';
export type ActionPlanSuggestionStatus = 'open' | 'done' | 'dismissed';

export interface ActionPlanInternalLinkRow {
  suggestionKey: string;
  score: number;
  sourceUrlId: string;
  sourceUrl: string;
  targetUrlId: string;
  targetUrl: string;
  suggestedAnchor: string;
  matchedTopic: string;
  where: {
    sentence: string;
    rationale: string;
  };
  reasons: string[];
  status: ActionPlanSuggestionStatus;
  statusUpdatedAt?: string | null;
  signals: {
    targetTraffic: number;
    contextualIncomingTarget: number;
    contextualOutgoingSource: number;
    sharedCluster: string[];
  };
}

export interface ActionPlanInternalLinksResponse {
  success: boolean;
  data: {
    domain: string;
    rows: ActionPlanInternalLinkRow[];
    meta: {
      generatedCount: number;
      visibleCount: number;
      includeCompleted: boolean;
      includeDismissed: boolean;
    };
  };
  timestamp: string;
}

export type UnclusteredPageRecommendation =
  | 'delete'
  | 'cluster_or_delete'
  | 'rewrite_redirect_or_delete';

export interface ActionPlanUnclusteredPageRow {
  suggestionKey: string;
  urlId: string;
  url: string;
  estimatedOrganicTraffic: number;
  topTopics: string[];
  recommendation: UnclusteredPageRecommendation;
  rationale: string;
  status: ActionPlanSuggestionStatus;
  statusUpdatedAt?: string | null;
  signals: {
    hasCluster: boolean;
    trafficBand: '0-2' | 'low' | 'high';
  };
}

export interface ActionPlanUnclusteredPagesResponse {
  success: boolean;
  data: {
    domain: string;
    rows: ActionPlanUnclusteredPageRow[];
    meta: {
      generatedCount: number;
      visibleCount: number;
      includeCompleted: boolean;
      includeDismissed: boolean;
    };
  };
  timestamp: string;
}

export type CannibalizationQualificationPath =
  | 'shared_keywords'
  | 'mixed_weak_keywords'
  | 'content_only'
  | 'page_intersection';

export type CannibalizationMergeGrade = 'merge' | 'review';

export interface ActionPlanCannibalizationSignals {
  sharedKeywordsCount: number;
  keywordJaccard: number;
  topicOverlap: number;
  contentTokenOverlap: number;
  contentTokenJaccard: number;
  bodyTokenOverlap?: number;
  bodyTokenJaccard?: number;
  uniqueContentTokenOverlap?: number;
  uniqueContentTokenJaccard?: number;
  trafficHigh: number;
  trafficLow: number;
  winnerUrlId: string;
  loserUrlId: string;
  primaryTopicSlugA?: string;
  primaryTopicSlugB?: string;
  pageIntersectionKeywordCount?: number;
  pageIntersectionSampleKeywords?: string[];
  pageIntersectionCost?: number;
  winnerRankedKeywordsCount: number;
  loserRankedKeywordsCount: number;
  mergeGrade: CannibalizationMergeGrade;
  qualificationPath: CannibalizationQualificationPath;
  claimsDiffer?: boolean;
  templateFamily?: boolean;
  boilerplateTokenOverlap?: number;
}

/** Stored when the pair is marked done via the decision buttons. */
export type CannibalPairResolution = 'delete' | 'merge_redirect' | 'keep_improve';

export interface ActionPlanCannibalizationRow {
  suggestionKey: string;
  score: number;
  urlAId: string;
  urlA: string;
  urlBId: string;
  urlB: string;
  winnerUrlId: string;
  loserUrlId: string;
  reasons: string[];
  signals: ActionPlanCannibalizationSignals;
  /** Present when `status` is done (which outcome was recorded). */
  cannibalResolution?: CannibalPairResolution | null;
  /** Optional rationale saved with the decision (shown in PDF export). */
  cannibalNotes?: string | null;
  preview: {
    titleA: string;
    titleB: string;
    snippetA: string;
    snippetB: string;
    metaDescriptionA?: string;
    metaDescriptionB?: string;
    headingsA: string[];
    headingsB: string[];
    loserWordCount?: number;
    loserAnalyzedAt?: string;
    winnerWordCount?: number;
    winnerAnalyzedAt?: string;
    loserLastContentModified?: string;
    loserLastContentModifiedSource?: string;
    winnerLastContentModified?: string;
    winnerLastContentModifiedSource?: string;
  };
  status: ActionPlanSuggestionStatus;
  statusUpdatedAt?: string | null;
}

export interface ActionPlanCannibalizationResponse {
  success: boolean;
  data: {
    domain: string;
    rows: ActionPlanCannibalizationRow[];
    meta: {
      generatedCount: number;
      visibleCount: number;
      includeCompleted: boolean;
      includeDismissed: boolean;
      includePageIntersection: boolean;
    };
  };
  timestamp: string;
}

export type ActionPlanV2RecommendedActionType =
  | 'fix_technical'
  | 'consolidate'
  | 'prioritize'
  | 'fix_intent'
  | 'add_internal_link'
  | 'cluster_assign';

export type ActionPlanV2SignalType =
  | 'cannibalization'
  | 'internal_link'
  | 'unclustered'
  | 'no_cluster';

export interface ActionPlanV2Row {
  urlId: string;
  url: string;
  priorityScore: number;
  recommendedAction: { type: ActionPlanV2RecommendedActionType; label: string };
  alternatives: [];
  signalTypes: ActionPlanV2SignalType[];
  explain: string[];
  metrics: {
    effectiveHealth: string;
    gapGate: string;
    realizationGap: number | null;
    realizationRatio: number | null;
    demandClaim: string | null;
    estimatedOrganicTraffic: number | null;
  };
  cannibal?: {
    suggestionKey: string;
    winnerUrlId: string;
    winnerUrl: string;
    loserUrlId: string;
    pairScore: number;
  };
  blockedActions?: Array<{ type: string; reason: string }>;
}

export interface ActionPlanV2Response {
  success: boolean;
  data: {
    domain: string;
    generatedAt: string;
    summary: {
      totalUrlsInPool: number;
      openQueueItems: number;
      p0Technical: number;
      p1Consolidate: number;
      p2Gap: number;
      p3Intent: number;
      p4Links: number;
      p5Cluster: number;
      themes: string[];
    };
    meta: {
      page: number;
      pageSize: number;
      totalItems: number;
      totalPages: number;
      decisionsApplied: number;
    };
    rows: ActionPlanV2Row[];
  };
  timestamp: string;
}

/** Matches `RankedKeywordsDomainSyncSummary` from the ranked-keywords-enrichment API. */
export interface RankedKeywordsEnrichmentSummary {
  matchedTrackedUrls: number;
  processed: number;
  updated: number;
  skippedFresh: number;
  skippedNoAnalysis: number;
  failed: number;
  estimatedCostSum: number;
  nextOffset: number | null;
  errors: Array<{ url: string; message: string }>;
}

@Injectable({ providedIn: 'root' })
export class InventoryPlanClustersService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  bootstrapDefaults(activeDomain: string): Observable<PlanTopicsResponse> {
    return this.http.post<PlanTopicsResponse>(`${this.API_URL}/domains/plan/bootstrap-defaults`, {
      activeDomain
    });
  }

  getClusters(activeDomain: string): Observable<PlanTopicsResponse> {
    const params = new URLSearchParams();
    params.set('activeDomain', activeDomain.trim());
    return this.http.get<PlanTopicsResponse>(`${this.API_URL}/domains/plan/topics?${params.toString()}`);
  }

  addCluster(activeDomain: string, topicSlug: string, label: string): Observable<PlanTopicsResponse> {
    return this.http.post<PlanTopicsResponse>(`${this.API_URL}/domains/plan/topics`, {
      activeDomain,
      topicSlug,
      label
    });
  }

  removeCluster(activeDomain: string, topicId: string): Observable<PlanTopicsResponse> {
    const params = new URLSearchParams();
    params.set('activeDomain', activeDomain.trim());
    return this.http.delete<PlanTopicsResponse>(
      `${this.API_URL}/domains/plan/topics/${encodeURIComponent(topicId)}?${params.toString()}`
    );
  }

  updateClusterLabel(activeDomain: string, topicId: string, label: string): Observable<PlanTopicsResponse> {
    return this.http.patch<PlanTopicsResponse>(
      `${this.API_URL}/domains/plan/topics/${encodeURIComponent(topicId)}`,
      {
        activeDomain,
        label
      }
    );
  }

  findUrlsForTopic(
    activeDomain: string,
    topicId: string,
    options: {
      search?: string;
      pathPrefixes?: string[];
      limit?: number;
      topicSlugOverride?: string;
      inclusionFilter?: InventoryTrackingFilter;
    }
  ): Observable<PlanTopicsResponse & { data: PlanTopicsResponse['data'] & { rows: any[]; addedCount: number; coreCount: number; searchedCount: number } }> {
    return this.http.post<any>(`${this.API_URL}/domains/plan/topics/${encodeURIComponent(topicId)}/find-urls`, {
      activeDomain,
      search: options.search || '',
      pathPrefixes: options.pathPrefixes || [],
      limit: options.limit ?? 200,
      topicSlugOverride: options.topicSlugOverride || '',
      inclusionFilter: options.inclusionFilter || 'tracked'
    });
  }

  getTopicCandidates(
    activeDomain: string,
    topicId: string,
    options: { search?: string; pathPrefixes?: string[]; limit?: number; inclusionFilter?: InventoryTrackingFilter }
  ): Observable<PlanTopicCandidatesResponse> {
    const params = new URLSearchParams();
    params.set('activeDomain', activeDomain.trim());
    if (options.search?.trim()) {
      params.set('search', options.search.trim());
    }
    for (const p of options.pathPrefixes || []) {
      params.append('pathPrefix', p);
    }
    if (typeof options.limit === 'number') {
      params.set('limit', String(options.limit));
    }
    params.set('inclusionFilter', options.inclusionFilter || 'tracked');
    return this.http.get<PlanTopicCandidatesResponse>(
      `${this.API_URL}/domains/plan/topics/${encodeURIComponent(topicId)}/candidates?${params.toString()}`
    );
  }

  updateUrlInclusionState(
    urlId: string,
    state: 'default' | 'force_include' | 'force_exclude',
    reason: string = ''
  ): Observable<{ success: boolean; data: { urlId: string; inventoryInclusionState: 'default' | 'force_include' | 'force_exclude' }; timestamp: string }> {
    return this.http.patch<{ success: boolean; data: { urlId: string; inventoryInclusionState: 'default' | 'force_include' | 'force_exclude' }; timestamp: string }>(
      `${this.API_URL}/urls/${encodeURIComponent(urlId)}/inclusion-state`,
      {
        state,
        reason
      }
    );
  }

  updateUrlsInclusionState(
    urlIds: string[],
    state: 'default' | 'force_include' | 'force_exclude',
    reason: string = ''
  ): Observable<{ success: boolean; data: { updatedCount: number; matchedCount: number; state: string; urlIds: string[] }; timestamp: string }> {
    return this.http.patch<{ success: boolean; data: { updatedCount: number; matchedCount: number; state: string; urlIds: string[] }; timestamp: string }>(
      `${this.API_URL}/urls/inclusion-state`,
      {
        urlIds,
        state,
        reason
      }
    );
  }

  addUrlsToCluster(
    activeDomain: string,
    topicId: string,
    items: Array<{ urlId?: string; url?: string }>,
    source: 'manual' | 'bulk' = 'manual'
  ): Observable<PlanTopicsResponse> {
    return this.http.post<PlanTopicsResponse>(
      `${this.API_URL}/domains/plan/topics/${encodeURIComponent(topicId)}/urls`,
      {
        activeDomain,
        source,
        items
      }
    );
  }

  removeUrlFromCluster(activeDomain: string, topicId: string, urlId: string): Observable<PlanTopicsResponse> {
    const params = new URLSearchParams();
    params.set('activeDomain', activeDomain.trim());
    return this.http.delete<PlanTopicsResponse>(
      `${this.API_URL}/domains/plan/topics/${encodeURIComponent(topicId)}/urls/${encodeURIComponent(urlId)}?${params.toString()}`
    );
  }

  updateClusterUrlClass(
    activeDomain: string,
    topicId: string,
    urlId: string,
    classOverride: PlanClusterClassValue
  ): Observable<PlanTopicsResponse & { data: PlanTopicsResponse['data'] & { updatedCount: number; classOverride: PlanClusterClassValue } }> {
    return this.http.patch<any>(
      `${this.API_URL}/domains/plan/topics/${encodeURIComponent(topicId)}/urls/${encodeURIComponent(urlId)}/class`,
      {
        activeDomain,
        classOverride
      }
    );
  }

  updateClusterUrlsClassBulk(
    activeDomain: string,
    topicId: string,
    urlIds: string[],
    classOverride: PlanClusterClassValue
  ): Observable<PlanTopicsResponse & { data: PlanTopicsResponse['data'] & { updatedCount: number; matchedCount: number; classOverride: PlanClusterClassValue } }> {
    return this.http.patch<any>(
      `${this.API_URL}/domains/plan/topics/${encodeURIComponent(topicId)}/urls/class`,
      {
        activeDomain,
        urlIds,
        classOverride
      }
    );
  }

  getInternalLinkActionPlan(
    domain: string,
    options: {
      limit?: number;
      urlId?: string;
      includeCompleted?: boolean;
      includeDismissed?: boolean;
    } = {}
  ): Observable<ActionPlanInternalLinksResponse> {
    const params = new URLSearchParams();
    params.set('domain', domain.trim());
    params.set('limit', String(options.limit ?? 500));
    if (options.urlId) params.set('urlId', options.urlId.trim());
    if (options.includeCompleted) params.set('includeCompleted', 'true');
    if (options.includeDismissed) params.set('includeDismissed', 'true');
    return this.http.get<ActionPlanInternalLinksResponse>(
      `${this.API_URL}/urls/action-plan/internal-links?${params.toString()}`
    );
  }

  updateInternalLinkActionPlanStatus(
    domain: string,
    suggestionKey: string,
    status: ActionPlanSuggestionStatus
  ): Observable<{ success: boolean; data: { suggestionKey: string; domain: string; status: ActionPlanSuggestionStatus }; timestamp: string }> {
    return this.http.patch<{ success: boolean; data: { suggestionKey: string; domain: string; status: ActionPlanSuggestionStatus }; timestamp: string }>(
      `${this.API_URL}/urls/action-plan/internal-links/${encodeURIComponent(suggestionKey)}`,
      {
        domain,
        status
      }
    );
  }

  getUnclusteredPageActionPlan(
    domain: string,
    options: { limit?: number; includeCompleted?: boolean; includeDismissed?: boolean } = {}
  ): Observable<ActionPlanUnclusteredPagesResponse> {
    const params = new URLSearchParams();
    params.set('domain', domain.trim());
    params.set('limit', String(options.limit ?? 100));
    if (options.includeCompleted) params.set('includeCompleted', 'true');
    if (options.includeDismissed) params.set('includeDismissed', 'true');
    return this.http.get<ActionPlanUnclusteredPagesResponse>(
      `${this.API_URL}/urls/action-plan/unclustered-pages?${params.toString()}`
    );
  }

  updateUnclusteredPageActionPlanStatus(
    domain: string,
    suggestionKey: string,
    status: ActionPlanSuggestionStatus
  ): Observable<{ success: boolean; data: { suggestionKey: string; domain: string; status: ActionPlanSuggestionStatus }; timestamp: string }> {
    return this.http.patch<{ success: boolean; data: { suggestionKey: string; domain: string; status: ActionPlanSuggestionStatus }; timestamp: string }>(
      `${this.API_URL}/urls/action-plan/unclustered-pages/${encodeURIComponent(suggestionKey)}`,
      {
        domain,
        status
      }
    );
  }

  getCannibalizationActionPlan(
    domain: string,
    options: {
      limit?: number;
      includeCompleted?: boolean;
      includeDismissed?: boolean;
      includePageIntersection?: boolean;
    } = {}
  ): Observable<ActionPlanCannibalizationResponse> {
    const params = new URLSearchParams();
    params.set('domain', domain.trim());
    params.set('limit', String(options.limit ?? 80));
    if (options.includeCompleted) params.set('includeCompleted', 'true');
    if (options.includeDismissed) params.set('includeDismissed', 'true');
    if (options.includePageIntersection) params.set('includePageIntersection', 'true');
    return this.http.get<ActionPlanCannibalizationResponse>(
      `${this.API_URL}/urls/action-plan/cannibalization?${params.toString()}`
    );
  }

  updateCannibalizationActionPlanStatus(
    domain: string,
    suggestionKey: string,
    status: ActionPlanSuggestionStatus,
    options?: { resolution?: CannibalPairResolution; notes?: string }
  ): Observable<{
    success: boolean;
    data: {
      suggestionKey: string;
      domain: string;
      status: ActionPlanSuggestionStatus;
      cannibalResolution?: CannibalPairResolution | null;
      cannibalNotes?: string | null;
    };
    timestamp: string;
  }> {
    const body: {
      domain: string;
      status: ActionPlanSuggestionStatus;
      resolution?: CannibalPairResolution;
      notes?: string;
    } = {
      domain,
      status
    };
    if (status === 'done' && options?.resolution) {
      body.resolution = options.resolution;
    }
    if (options && 'notes' in options && options.notes !== undefined) {
      body.notes = options.notes;
    }
    return this.http.patch<
      {
        success: boolean;
        data: {
          suggestionKey: string;
          domain: string;
          status: ActionPlanSuggestionStatus;
          cannibalResolution?: CannibalPairResolution | null;
          cannibalNotes?: string | null;
        };
        timestamp: string;
      }
    >(`${this.API_URL}/urls/action-plan/cannibalization/${encodeURIComponent(suggestionKey)}`, body);
  }

  /** Removes all cannibalization_pair decisions for the domain (every pair returns to open after reload). */
  getActionPlanV2(
    domain: string,
    options: {
      page?: number;
      pageSize?: number;
      urlId?: string;
      includeCompleted?: boolean;
      includeDismissed?: boolean;
    } = {}
  ): Observable<ActionPlanV2Response> {
    const params = new URLSearchParams();
    params.set('domain', domain.trim());
    params.set('page', String(options.page ?? 1));
    params.set('pageSize', String(options.pageSize ?? 50));
    if (options.urlId) params.set('urlId', options.urlId);
    if (options.includeCompleted) params.set('includeCompleted', 'true');
    if (options.includeDismissed) params.set('includeDismissed', 'true');
    return this.http.get<ActionPlanV2Response>(
      `${this.API_URL}/urls/action-plan/v2?${params.toString()}`
    );
  }

  resetAllCannibalizationDecisions(domain: string): Observable<{
    success: boolean;
    data: { domain: string; deletedCount: number };
    timestamp: string;
  }> {
    const params = new URLSearchParams();
    params.set('domain', domain.trim());
    return this.http.delete<{
      success: boolean;
      data: { domain: string; deletedCount: number };
      timestamp: string;
    }>(`${this.API_URL}/urls/action-plan/cannibalization/decisions?${params.toString()}`);
  }

  runRankedKeywordsEnrichment(
    domain: string,
    body: {
      force?: boolean;
      ttlDays?: number;
      maxPages?: number;
      offset?: number;
      delayMs?: number;
      keywordLimit?: number;
    } = {}
  ): Observable<{ success: boolean; message?: string; data: RankedKeywordsEnrichmentSummary; timestamp?: string }> {
    return this.http.post<{ success: boolean; message?: string; data: RankedKeywordsEnrichmentSummary; timestamp?: string }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain.trim())}/process/ranked-keywords-enrichment`,
      body
    );
  }

  getIncomingLinks(urlId: string, summaryOnly = false): Observable<{
    success: boolean;
    data: {
      targetUrl: string;
      incomingLinks: Array<{
        sourceUrl: string;
        sourcePageTitle?: string;
        anchorText: string;
        linkType: string;
        confidence: number;
        dofollow: boolean;
        isBroken: boolean;
        targetUrl?: string;
        targetRelativeUrl?: string;
      }>;
      metadata: {
        totalIncomingLinks: number;
        contextualLinks: number;
        structuralLinks?: number;
        samePageLinks?: number;
        unknownLinks?: number;
        requiresDomainCrawl?: boolean;
        domain?: string;
      };
    };
    timestamp: string;
  }> {
    const params = new URLSearchParams();
    if (summaryOnly) params.set('summary', 'true');
    return this.http.get<{
      success: boolean;
      data: {
        targetUrl: string;
        incomingLinks: Array<{
          sourceUrl: string;
          sourcePageTitle?: string;
          anchorText: string;
          linkType: string;
          confidence: number;
          dofollow: boolean;
          isBroken: boolean;
          targetUrl?: string;
          targetRelativeUrl?: string;
        }>;
        metadata: {
          totalIncomingLinks: number;
          contextualLinks: number;
          structuralLinks?: number;
          samePageLinks?: number;
          unknownLinks?: number;
          requiresDomainCrawl?: boolean;
          domain?: string;
        };
      };
      timestamp: string;
    }>(`${this.API_URL}/urls/${encodeURIComponent(urlId)}/incoming-links?${params.toString()}`);
  }
}
